import { Mail, Phone, Plus, Star, X as XIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { assignablePeople } from "@/lib/roster";
import { InfoCard } from "./WOGeneralCard";

const ContactLinks = ({ email, phone }) => (
  <>
    {email && <a href={`mailto:${email}`} className="text-slate-400 hover:text-brand shrink-0" title={email}><Mail className="w-3.5 h-3.5" /></a>}
    {phone && <a href={`tel:${phone}`} className="text-slate-400 hover:text-brand shrink-0" title={phone}><Phone className="w-3.5 h-3.5" /></a>}
  </>
);

const Row = ({ role, children, testId, top }) => (
  <div className={`grid grid-cols-[120px_1fr] gap-2 text-sm ${top ? "items-start" : "items-center"}`} data-testid={testId}>
    <span className={`text-slate-500 ${top ? "pt-1" : ""}`}>{role}</span>
    <span className="flex items-center gap-2 min-w-0 flex-wrap">{children}</span>
  </div>
);

const Group = ({ title, action, children }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <h4 className="text-[11px] font-semibold tracking-wide text-slate-500">{title}</h4>
      {action}
    </div>
    {children}
  </div>
);

const TRIGGER = "h-7 w-48 text-xs";

// "Directory" card on the Info tab — ABS Team + Customer Team. The people selects live right in the
// card (no edit mode): change, add or remove straight from the main view.
export function WODirectoryCard({
  form, setForm, isEditable, peopleEditable,
  clientSites, siteContactsOverlay, accountManagers, projectManagers, technicians,
  onAddContactClick, clientBillingContact,
}) {
  const site = clientSites.find((s) => s.id === form.site_id);
  const contacts = [...(site?.contacts || []), ...((siteContactsOverlay || {})[form.site_id] || [])];
  const siteContact = contacts.find((c) => c.id === form.site_contact_id);
  const am = (accountManagers || []).find((a) => a.name === form.account_manager);
  const pm = (projectManagers || []).find((p) => p.id === form.project_manager_id);
  const assignedIds = form.assigned_tech_ids || [];
  const sortedAssigned = form.lead_tech_id && assignedIds.includes(form.lead_tech_id)
    ? [form.lead_tech_id, ...assignedIds.filter((id) => id !== form.lead_tech_id)] : assignedIds;
  const unassigned = (technicians || []).filter((t) => !assignedIds.includes(t.id));

  const removeTech = (tid) => {
    const ids = assignedIds.filter((id) => id !== tid);
    setForm((prev) => ({ ...prev, assigned_tech_ids: ids, assigned_tech_id: ids[0] || null, lead_tech_id: prev.lead_tech_id === tid ? null : prev.lead_tech_id }));
  };
  const addTech = (tid) => {
    if (!tid || assignedIds.includes(tid)) return;
    const ids = [...assignedIds, tid];
    setForm((prev) => ({ ...prev, assigned_tech_ids: ids, assigned_tech_id: ids[0] || null }));
  };

  return (
    <InfoCard title="Directory" testId="wo-directory-card" className="h-full">
      <div className="space-y-6" data-testid="wo-directory-read">
        <Group title="ABS Team">
          <Row role="Account Manager" testId="wo-dir-account-manager">
            <Select value={form.account_manager || "__none__"} disabled={!peopleEditable}
              onValueChange={(v) => {
                if (v === "__none__") setForm((prev) => ({ ...prev, account_manager: "", account_manager_email: "" }));
                else { const a = accountManagers.find((x) => x.name === v); setForm((prev) => ({ ...prev, account_manager: v, account_manager_email: a?.email || "" })); }
              }}>
              <SelectTrigger className={TRIGGER} data-testid="select-account-manager"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="text-slate-400 italic text-xs">None</SelectItem>
                {assignablePeople(accountManagers, form.account_manager).map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <ContactLinks email={form.account_manager_email || am?.email} phone={am?.phone} />
          </Row>
          <Row role="Project Manager" testId="wo-dir-project-manager">
            <Select value={form.project_manager_id || "none"} disabled={!peopleEditable}
              onValueChange={(v) => {
                if (v === "none") setForm((prev) => ({ ...prev, project_manager_id: null, project_manager_name: "" }));
                else { const p = projectManagers.find((x) => x.id === v); setForm((prev) => ({ ...prev, project_manager_id: v, project_manager_name: p?.name || "" })); }
              }}>
              <SelectTrigger className={`${TRIGGER} ${!form.project_manager_id ? "border-rose-300 bg-rose-50/40" : ""}`} data-testid="select-project-manager"
                title={!form.project_manager_id ? "Required — Foundation project manager" : undefined}>
                <SelectValue placeholder="Required" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-slate-400 italic text-xs">None</SelectItem>
                {assignablePeople(projectManagers, form.project_manager_id, "id").map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <ContactLinks email={pm?.email} phone={pm?.phone} />
          </Row>
          <Row role="Technicians" testId="wo-dir-techs" top>
            {sortedAssigned.map((tid) => {
              const t = (technicians || []).find((x) => x.id === tid);
              if (!t) return null;
              const isLead = form.lead_tech_id === tid;
              const color = t.color || "#3b82f6";
              return (
                <span key={tid} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs cursor-pointer ${isLead ? "font-bold" : ""}`}
                  style={{ borderColor: `${color}66`, color, backgroundColor: `${color}${isLead ? "25" : "12"}` }}
                  onClick={() => peopleEditable && setForm((prev) => ({ ...prev, lead_tech_id: prev.lead_tech_id === tid ? null : tid }))}
                  title={isLead ? "Lead tech (click to clear)" : "Click to make lead tech"} data-testid={`wo-dir-tech-${t.id}`}>
                  {isLead && <Star className="w-3 h-3 fill-current" />}{t.name}
                  {peopleEditable && (
                    <button type="button" className="ml-0.5 opacity-50 hover:opacity-100" onClick={(e) => { e.stopPropagation(); removeTech(tid); }}
                      aria-label={`Remove ${t.name}`} data-testid={`remove-tech-${tid}`}>
                      <XIcon className="w-3 h-3" />
                    </button>
                  )}
                </span>
              );
            })}
            {peopleEditable ? (
              <Select value="" onValueChange={addTech}>
                <SelectTrigger className="h-7 w-auto min-w-[80px] text-xs border-dashed px-2" data-testid="select-add-tech"><span className="text-slate-500">+ Add</span></SelectTrigger>
                <SelectContent>
                  {unassigned.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: t.color || "#3b82f6" }} />{t.name}</span>
                    </SelectItem>
                  ))}
                  {unassigned.length === 0 && <div className="text-xs text-slate-400 py-2 px-3">All techs assigned</div>}
                </SelectContent>
              </Select>
            ) : (sortedAssigned.length === 0 && <span className="text-slate-400">—</span>)}
          </Row>
        </Group>

        <Group title="Customer Team"
          action={isEditable && form.site_id && (
            <button type="button" onClick={onAddContactClick} className="inline-flex h-6 w-6 items-center justify-center rounded-md border text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="Add a site contact" data-testid="wo-dir-add-contact">
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}>
          <Row role="Site Contact" testId="wo-dir-site-contact">
            <Select value={form.site_contact_id || "__none__"} disabled={!isEditable || !form.site_id}
              onValueChange={(v) => {
                if (v === "__none__") setForm((prev) => ({ ...prev, site_contact_id: "" }));
                else if (v === "__add__") onAddContactClick();
                else setForm((prev) => ({ ...prev, site_contact_id: v }));
              }}>
              <SelectTrigger className={TRIGGER} data-testid="select-site-contact"><SelectValue placeholder={form.site_id ? "—" : "Pick a site first"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="text-slate-400 italic text-xs">None</SelectItem>
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.is_primary ? " ★" : ""}</SelectItem>)}
                {form.site_id && isEditable && <SelectItem value="__add__" className="text-brand">+ Add new contact</SelectItem>}
              </SelectContent>
            </Select>
            <ContactLinks email={siteContact?.email} phone={siteContact?.phone} />
          </Row>
          <Row role="Billing" testId="wo-dir-billing-contact">
            <span className={`truncate ${clientBillingContact?.name ? "text-slate-800" : "text-slate-400"}`}>{clientBillingContact?.name || "—"}</span>
            <ContactLinks email={clientBillingContact?.email} phone={clientBillingContact?.phone} />
          </Row>
          {form.site_id && contacts.length === 0 && <div className="text-xs text-slate-400">No contacts on this site yet — add one with the + button.</div>}
        </Group>
      </div>
    </InfoCard>
  );
}
